import { FC } from 'react'
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
} from '@mui/material'
import {
  Search as SearchIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from '@mui/icons-material'
import type { SortOption, SortOrder } from '../hooks/use-inventory-search-and-sort.ts'

export interface InventorySearchSortControlsProps {
  searchQuery: string
  onSearchChange: (query: string) => void
  sortBy: SortOption
  onSortByChange: (sortBy: SortOption) => void
  sortOrder: SortOrder
  onToggleSortOrder: () => void
  searchPlaceholder?: string
  showSearch?: boolean
  showSort?: boolean
}

const SORT_OPTIONS = [
  { value: 'name' as const, label: 'Name' },
  { value: 'price' as const, label: 'Price' },
  { value: 'updatedAt' as const, label: 'Last updated' },
  { value: 'createdAt' as const, label: 'Created date' },
]

export const InventorySearchSortControls: FC<InventorySearchSortControlsProps> = ({
  searchQuery,
  onSearchChange,
  sortBy,
  onSortByChange,
  sortOrder,
  onToggleSortOrder,
  searchPlaceholder = 'Search items...',
  showSearch = true,
  showSort = true,
}) => {
  return (
    <Box display="flex" gap={2} alignItems="center">
      {showSearch && (
        <TextField
          placeholder={searchPlaceholder}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          variant="outlined"
          size="small"
          sx={{ flexGrow: 1 }}
          InputProps={{
            startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
          }}
        />
      )}

      {showSort && (
        <>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Sort by</InputLabel>
            <Select
              value={sortBy}
              onChange={(e) => onSortByChange(e.target.value as SortOption)}
              label="Sort by"
            >
              {SORT_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <IconButton
            onClick={onToggleSortOrder}
            size="small"
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
            }}
            title={`Sort ${sortOrder === 'asc' ? 'ascending' : 'descending'}`}
          >
            {sortOrder === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
          </IconButton>
        </>
      )}
    </Box>
  )
}
